import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
  { id: 'fashion', label: 'Fashion & Apparel', desc: 'Pakaian, aksesoris, sepatu', emoji: '👗' },
  { id: 'fnb', label: 'Food & Beverage', desc: 'Restoran, cafe, FMCG makanan', emoji: '☕' },
  { id: 'retail', label: 'Retail & Toko', desc: 'Minimarket, kelontong, elektronik', emoji: '🛍️' },
  { id: 'manufaktur', label: 'Manufaktur', desc: 'Pabrik & produksi industri', emoji: '⚙️' },
  { id: 'umum', label: 'Bisnis Lainnya', desc: 'Jasa, digital, umum', emoji: '💡' },
];

const CURRENCIES = [
  { value: 'IDR', label: 'IDR, Rupiah' },
  { value: 'USD', label: 'USD, Dollar' },
  { value: 'SGD', label: 'SGD, Singapore Dollar' },
  { value: 'MYR', label: 'MYR, Ringgit' },
];

export default function CompanySetup() {
  const { token, fetchUser, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [industry, setIndustry] = useState('');
  const [form, setForm] = useState({
    company_name: '', currency: 'IDR',
    phone: '', email: '', npwp: '', address: '',
  });
  const [loading, setLoading] = useState(false);
  const [prefilling, setPrefilling] = useState(true);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false); // welcome screen

  // Pre-fill from existing profile
  useEffect(() => {
    if (!token) { setPrefilling(false); return; }
    axios.get(`${API}/tenant/company-profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const d = res.data;
        if (d.industry) {
          setIndustry(d.industry);
          setStep(2);
        }
        setForm({
          company_name: d.company_name || d.tenant_name || '',
          currency: d.currency || 'IDR',
          phone: d.phone || '',
          email: d.email || '',
          npwp: d.npwp || '',
          address: d.address || '',
        });
      })
      .catch(() => {})
      .finally(() => setPrefilling(false));
  }, [token]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleNext = () => {
    if (!industry) { setError('Pilih jenis industri terlebih dahulu.'); return; }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!form.company_name.trim()) { setError('Nama perusahaan wajib diisi.'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.put(`${API}/tenant/company-profile`, { industry, ...form }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (typeof fetchUser === 'function') await fetchUser(token);
      setDone(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Gagal menyimpan. Coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const selectedIndustry = INDUSTRIES.find(i => i.id === industry);

  // Welcome screen
  if (done) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8fafb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      }}>
        <div style={{ width: '100%', maxWidth: '480px', textAlign: 'center' }}>
          <div style={{
            background: 'white', borderRadius: '24px',
            boxShadow: '0 4px 32px rgba(0,0,0,0.08)',
            padding: '48px 40px',
            border: '1px solid #eef0f2',
          }}>
            {/* Animated checkmark */}
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: '#f0fdf4', border: '2px solid #86efac',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
              animation: 'pop 0.4s ease',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h1 style={{ margin: '0 0 12px', fontSize: '26px', fontWeight: '700', color: '#111' }}>
              Selamat datang di Stockara!
            </h1>
            <p style={{ margin: '0 0 8px', fontSize: '15px', color: '#555', lineHeight: 1.6 }}>
              Profil bisnis kamu sudah tersimpan.
              {user?.name ? ` Semangat, kak ${user.name.split(' ')[0]}!` : ' Semangat!'}
            </p>
            <p style={{ margin: '0 0 32px', fontSize: '14px', color: '#888' }}>
              Template AI CS dan pengaturan bisnis sudah disiapkan sesuai industri{' '}
              <strong style={{ color: '#1A5733' }}>{selectedIndustry?.label || industry}</strong>.
            </p>

            {/* What's ready */}
            <div style={{ textAlign: 'left', marginBottom: '32px' }}>
              {[
                { icon: '📦', text: 'Inventori siap untuk tambah produk pertama' },
                { icon: '🤖', text: 'AI CS sudah dikonfigurasi sesuai bisnis kamu' },
                { icon: '📊', text: 'Dashboard laporan dan penjualan aktif' },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 0',
                  borderBottom: i < 2 ? '1px solid #f1f5f9' : 'none',
                }}>
                  <span style={{ fontSize: '18px' }}>{item.icon}</span>
                  <p style={{ margin: 0, fontSize: '14px', color: '#444' }}>{item.text}</p>
                </div>
              ))}
            </div>

            <div style={{
              background: '#f0f7f3', borderRadius: '10px', padding: '12px 16px',
              fontSize: '13px', color: '#555',
            }}>
              Mengarahkan ke dashboard dalam beberapa detik...
            </div>
          </div>
        </div>
        <style>{`@keyframes pop { 0% { transform: scale(0.6); opacity: 0; } 80% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      </div>
    );
  }

  // Loading prefill
  if (prefilling) {
    return (
      <div style={{
        minHeight: '100vh', background: '#f8fafb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px', height: '40px', border: '3px solid #e2e8f0',
            borderTopColor: '#1A5733', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>Memuat data bisnis...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #f8fafc 50%, #ecfdf5 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: '520px' }}>

        {/* Logo & Brand */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '10px',
            background: 'white', borderRadius: '16px',
            padding: '10px 20px', boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: '800', fontSize: '16px',
            }}>S</div>
            <span style={{ fontWeight: '700', fontSize: '18px', color: '#111827' }}>Stockara</span>
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: 'white', borderRadius: '24px',
          boxShadow: '0 4px 32px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>

          {/* Progress Bar */}
          <div style={{ height: '3px', background: '#f1f5f9' }}>
            <div style={{
              height: '100%', width: step === 1 ? '50%' : '100%',
              background: 'linear-gradient(90deg, #10b981, #059669)',
              transition: 'width 0.4s ease',
              borderRadius: '99px',
            }} />
          </div>

          <div style={{ padding: '36px 40px 40px' }}>

            {/* Step indicator */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '24px' }}>
              {[1, 2].map(s => (
                <div key={s} style={{
                  height: '4px', flex: 1, borderRadius: '99px',
                  background: s <= step ? '#10b981' : '#e2e8f0',
                  transition: 'background 0.3s',
                }} />
              ))}
            </div>

            {step === 1 ? (
              <>
                <div style={{ marginBottom: '28px' }}>
                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#10b981', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Langkah 1 dari 2
                  </p>
                  <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 8px' }}>
                    Bisnis kamu bergerak di bidang apa?
                  </h1>
                  <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                    Kami akan mengaktifkan fitur yang paling relevan untuk kamu.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind.id}
                      onClick={() => { setIndustry(ind.id); setError(''); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '14px',
                        padding: '14px 18px', borderRadius: '14px', cursor: 'pointer',
                        border: industry === ind.id ? '2px solid #10b981' : '1.5px solid #e2e8f0',
                        background: industry === ind.id ? '#f0fdf4' : 'white',
                        transition: 'all 0.15s ease', textAlign: 'left',
                        transform: industry === ind.id ? 'scale(1.01)' : 'scale(1)',
                        boxShadow: industry === ind.id ? '0 2px 12px rgba(16,185,129,0.15)' : 'none',
                      }}
                    >
                      <span style={{ fontSize: '24px', lineHeight: 1 }}>{ind.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          margin: 0, fontWeight: '600', fontSize: '14px',
                          color: industry === ind.id ? '#065f46' : '#1e293b',
                        }}>{ind.label}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>{ind.desc}</p>
                      </div>
                      {industry === ind.id && (
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%',
                          background: '#10b981', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flexShrink: 0,
                        }}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {error && (
                  <div style={{
                    background: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: '10px', padding: '12px 14px',
                    fontSize: '13px', color: '#dc2626', marginBottom: '16px',
                  }}>{error}</div>
                )}

                <button
                  onClick={handleNext}
                  style={{
                    width: '100%', padding: '14px', borderRadius: '12px',
                    background: industry ? 'linear-gradient(135deg, #10b981, #059669)' : '#e2e8f0',
                    color: industry ? 'white' : '#94a3b8',
                    fontWeight: '600', fontSize: '15px', border: 'none',
                    cursor: industry ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    boxShadow: industry ? '0 4px 16px rgba(16,185,129,0.3)' : 'none',
                  }}
                >
                  Lanjut →
                </button>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '28px' }}>
                  <button
                    onClick={() => setStep(1)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#64748b', fontSize: '13px', padding: 0, marginBottom: '16px',
                    }}
                  >
                    ← Kembali
                  </button>

                  {/* Selected industry badge */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                    borderRadius: '99px', padding: '6px 14px', marginBottom: '16px',
                  }}>
                    <span style={{ fontSize: '16px' }}>{selectedIndustry?.emoji}</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#065f46' }}>
                      {selectedIndustry?.label}
                    </span>
                  </div>

                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#10b981', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Langkah 2 dari 2
                  </p>
                  <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 8px' }}>
                    Detail perusahaan
                  </h1>
                  <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                    Info ini digunakan untuk laporan dan dokumen bisnis kamu.
                  </p>
                </div>

                {/* Form fields */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Nama Perusahaan <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    name="company_name"
                    value={form.company_name}
                    onChange={handleChange}
                    placeholder="Contoh: PT Maju Jaya"
                    style={{
                      width: '100%', padding: '11px 14px', borderRadius: '10px',
                      border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                      outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
                    }}
                    onFocus={e => e.target.style.borderColor = '#10b981'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {/* Currency */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Mata Uang
                  </label>
                  <select
                    name="currency" value={form.currency} onChange={handleChange}
                    style={{
                      width: '100%', padding: '11px 14px', borderRadius: '10px',
                      border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                      background: 'white', outline: 'none', boxSizing: 'border-box',
                    }}
                  >
                    {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                {/* Phone & Email */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  {[
                    { name: 'phone', label: 'Telepon', placeholder: '08xxxxxxxxxx' },
                    { name: 'email', label: 'Email Bisnis', placeholder: 'email@bisnis.com' },
                  ].map(field => (
                    <div key={field.name}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                        {field.label}
                      </label>
                      <input
                        name={field.name} value={form[field.name]}
                        onChange={handleChange} placeholder={field.placeholder}
                        style={{
                          width: '100%', padding: '11px 14px', borderRadius: '10px',
                          border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                          outline: 'none', boxSizing: 'border-box',
                        }}
                        onFocus={e => e.target.style.borderColor = '#10b981'}
                        onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                      />
                    </div>
                  ))}
                </div>

                {/* NPWP */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    NPWP / Tax ID <span style={{ fontWeight: '400', color: '#94a3b8' }}>(opsional)</span>
                  </label>
                  <input
                    name="npwp" value={form.npwp} onChange={handleChange}
                    placeholder="00.000.000.0-000.000"
                    style={{
                      width: '100%', padding: '11px 14px', borderRadius: '10px',
                      border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                    onFocus={e => e.target.style.borderColor = '#10b981'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {/* Address */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Alamat <span style={{ fontWeight: '400', color: '#94a3b8' }}>(opsional)</span>
                  </label>
                  <textarea
                    name="address" value={form.address} onChange={handleChange}
                    placeholder="Jl. Contoh No. 1, Jakarta" rows={2}
                    style={{
                      width: '100%', padding: '11px 14px', borderRadius: '10px',
                      border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                      outline: 'none', boxSizing: 'border-box', resize: 'none',
                      fontFamily: 'inherit',
                    }}
                    onFocus={e => e.target.style.borderColor = '#10b981'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {error && (
                  <div style={{
                    background: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: '10px', padding: '12px 14px',
                    fontSize: '13px', color: '#dc2626', marginBottom: '16px',
                  }}>{error}</div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{
                    width: '100%', padding: '14px', borderRadius: '12px',
                    background: loading ? '#e2e8f0' : 'linear-gradient(135deg, #10b981, #059669)',
                    color: loading ? '#94a3b8' : 'white',
                    fontWeight: '600', fontSize: '15px', border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: loading ? 'none' : '0 4px 16px rgba(16,185,129,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                >
                  {loading ? (
                    <>
                      <svg style={{ animation: 'spin 1s linear infinite' }} width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10"/>
                      </svg>
                      Menyimpan...
                    </>
                  ) : 'Mulai Pakai Stockara →'}
                </button>

                <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '16px', marginBottom: 0 }}>
                  Data ini bisa diubah kapan saja di Settings
                </p>
              </>
            )}
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '20px' }}>
          © 2026 Stockara · Platform bisnis dengan stok, kasir, laporan, dan AI
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
